(() => {
  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
  const lerp = (a, b, t) => a + (b - a) * t;
  const easeInOut = (t) =>
    t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  const easeOut = (t) => 1 - Math.pow(1 - t, 3);

  const hashStr = (str) => {
    let h = 2166136261;
    for (let i = 0; i < str.length; i += 1) {
      h ^= str.charCodeAt(i);
      h = Math.imul(h, 16777619);
    }
    return h >>> 0;
  };

  const makeRand = (seed) => {
    let s = seed >>> 0;
    return () => {
      s ^= s << 13;
      s >>>= 0;
      s ^= s >> 17;
      s >>>= 0;
      s ^= s << 5;
      s >>>= 0;
      return (s >>> 0) / 4294967296;
    };
  };

  class MetagateImmersiveLanding {
    constructor(root) {
      this.root = root;
      this.ticking = false;
      this.mouse = { x: 0, y: 0, targetX: 0, targetY: 0 };

      this.prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      this.desktopPointer = window.matchMedia("(hover: hover) and (pointer: fine)").matches;
      this.mobileQuery = window.matchMedia("(max-width: 749px)");
      this.isMobile = this.mobileQuery.matches;

      this.introTimer = null;
      this.copyTimer = null;
      this.blogClampTimer = null;
      this.blogParallaxFrame = null;
      this.blogHighlightFrame = null;

      this.blogPointerX = window.innerWidth * 0.5;
      this.blogPointerY = window.innerHeight * 0.5;

      this.boundHotspots = new Set();
      this.boundShatterLines = new Set();
      this.boundTeamCards = new Set();

      this.handleScroll = this.handleScroll.bind(this);
      this.handleResize = this.handleResize.bind(this);
      this.handleMouseMove = this.handleMouseMove.bind(this);
      this.handleHotspotEnter = this.handleHotspotEnter.bind(this);
      this.handleHotspotLeave = this.handleHotspotLeave.bind(this);
      this.handleSectionLoad = this.handleSectionLoad.bind(this);
      this.handleShatterEnter = this.handleShatterEnter.bind(this);
      this.handleShatterLeave = this.handleShatterLeave.bind(this);
      this.handleTeamCardClick = this.handleTeamCardClick.bind(this);
      this.handleTeamCardKeydown = this.handleTeamCardKeydown.bind(this);
      this.handleTeamCardMouseMove = this.handleTeamCardMouseMove.bind(this);
      this.handleTeamOverlayClick = this.handleTeamOverlayClick.bind(this);
      this.handleDocumentKeydown = this.handleDocumentKeydown.bind(this);
      this.handleDocumentClick = this.handleDocumentClick.bind(this);
      this.handleTeamCardMouseEnter = this.handleTeamCardMouseEnter.bind(this);
      this.handleTeamCardMouseLeave = this.handleTeamCardMouseLeave.bind(this);
      this.handleBlogPointerMove = this.handleBlogPointerMove.bind(this);
      this.handleBlogPointerLeave = this.handleBlogPointerLeave.bind(this);

      this.refreshDynamicElements();
      this.init();
    }

    refreshDynamicElements() {
      this.hotspots = [...this.root.querySelectorAll(".mg-hotspot")];
      this.mobileTags = [...this.root.querySelectorAll("[data-mobile-tag]")];
      this.shatterLines = [...this.root.querySelectorAll(".js-shatter, .js-room-shatter")];
      this.teamStage = this.root.querySelector(".js-team-stage");
      this.teamOverlay = this.root.querySelector(".js-team-overlay");
      this.teamCards = [...this.root.querySelectorAll(".js-team-card")];
      this.blogScene = this.root.querySelector(".mg-scene--blog");
      this.blogGrid = this.root.querySelector("[data-mg-blog-grid]");
      this.blogCards = [...this.root.querySelectorAll("[data-mg-blog-card]")];

      this.blogPointerEnabled =
        this.desktopPointer &&
        !this.prefersReducedMotion &&
        !this.isMobile &&
        !!this.blogGrid;
    }

    init() {
      if (this.root.dataset.mgInitialized === "true") return;

      this.root.dataset.mgInitialized = "true";
      this.prepareShatterText();
      this.bindResponsiveEvents();

      if (this.isMobile) {
        this.initMobile();
        return;
      }

      this.initDesktop();
    }

    initDesktop() {
      this.root.classList.remove("is-mobile");
      this.bindGlobalEvents();
      this.bindDynamicEvents();
      this.setTeamCardLayout();
      this.refreshTeamCardMetrics();
      this.initBlogCards();

      if (this.prefersReducedMotion || window.Shopify?.designMode) {
        this.root.classList.add("is-loaded");
        this.root.style.setProperty("--copy-opacity", "1");
      } else {
        this.introTimer = window.setTimeout(() => {
          this.root.classList.add("is-loaded");
        }, 100);

        this.copyTimer = window.setTimeout(() => {
          this.root.style.setProperty("--copy-opacity", "1");
        }, 300);
      }

      this.requestTick();
    }

    initMobile() {
      this.root.classList.add("is-loaded", "is-mobile");
      this.bindGlobalEvents();
      this.bindDynamicEvents();
      this.setTeamCardLayout();
      this.refreshTeamCardMetrics();
      this.requestTick();
    }

    bindResponsiveEvents() {
      window.addEventListener("resize", this.handleResize);

      if (this.mobileQuery.addEventListener) {
        this.mobileQuery.addEventListener("change", this.handleResize);
      }
    }

    unbindResponsiveEvents() {
      window.removeEventListener("resize", this.handleResize);

      if (this.mobileQuery.removeEventListener) {
        this.mobileQuery.removeEventListener("change", this.handleResize);
      }
    }

    bindGlobalEvents() {
      window.addEventListener("scroll", this.handleScroll, { passive: true });
      window.addEventListener("mousemove", this.handleMouseMove, { passive: true });
      document.addEventListener("shopify:section:load", this.handleSectionLoad);
      document.addEventListener("keydown", this.handleDocumentKeydown);
      document.addEventListener("click", this.handleDocumentClick);
    }

    unbindGlobalEvents() {
      window.removeEventListener("scroll", this.handleScroll);
      window.removeEventListener("mousemove", this.handleMouseMove);
      document.removeEventListener("shopify:section:load", this.handleSectionLoad);
      document.removeEventListener("keydown", this.handleDocumentKeydown);
      document.removeEventListener("click", this.handleDocumentClick);
    }

    bindDynamicEvents() {
      this.hotspots.forEach((hotspot) => {
        if (this.boundHotspots.has(hotspot)) return;

        hotspot.addEventListener("mouseenter", this.handleHotspotEnter);
        hotspot.addEventListener("focus", this.handleHotspotEnter);
        hotspot.addEventListener("mouseleave", this.handleHotspotLeave);
        hotspot.addEventListener("blur", this.handleHotspotLeave);

        this.boundHotspots.add(hotspot);
      });

      this.shatterLines.forEach((line) => {
        if (this.boundShatterLines.has(line)) return;

        line.addEventListener("mouseenter", this.handleShatterEnter);
        line.addEventListener("mouseleave", this.handleShatterLeave);
        line.addEventListener("focusin", this.handleShatterEnter);
        line.addEventListener("focusout", this.handleShatterLeave);

        this.boundShatterLines.add(line);
      });

      this.teamCards.forEach((card) => {
        if (this.boundTeamCards.has(card)) return;

        card.addEventListener("click", this.handleTeamCardClick);
        card.addEventListener("keydown", this.handleTeamCardKeydown);
        card.addEventListener("mouseenter", this.handleTeamCardMouseEnter);
        card.addEventListener("mousemove", this.handleTeamCardMouseMove);
        card.addEventListener("mouseleave", this.handleTeamCardMouseLeave);

        this.boundTeamCards.add(card);
      });

      if (this.teamOverlay) {
        this.teamOverlay.removeEventListener("click", this.handleTeamOverlayClick);
        this.teamOverlay.addEventListener("click", this.handleTeamOverlayClick);
      }

      if (this.blogGrid && this.blogPointerEnabled) {
        this.blogGrid.removeEventListener("pointermove", this.handleBlogPointerMove);
        this.blogGrid.removeEventListener("pointerleave", this.handleBlogPointerLeave);
        this.blogGrid.addEventListener("pointermove", this.handleBlogPointerMove, { passive: true });
        this.blogGrid.addEventListener("pointerleave", this.handleBlogPointerLeave);
      }
    }

    unbindDynamicEvents() {
      this.boundHotspots.forEach((hotspot) => {
        hotspot.removeEventListener("mouseenter", this.handleHotspotEnter);
        hotspot.removeEventListener("focus", this.handleHotspotEnter);
        hotspot.removeEventListener("mouseleave", this.handleHotspotLeave);
        hotspot.removeEventListener("blur", this.handleHotspotLeave);
      });
      this.boundHotspots.clear();

      this.boundShatterLines.forEach((line) => {
        line.removeEventListener("mouseenter", this.handleShatterEnter);
        line.removeEventListener("mouseleave", this.handleShatterLeave);
        line.removeEventListener("focusin", this.handleShatterEnter);
        line.removeEventListener("focusout", this.handleShatterLeave);
      });
      this.boundShatterLines.clear();

      this.boundTeamCards.forEach((card) => {
        card.removeEventListener("click", this.handleTeamCardClick);
        card.removeEventListener("keydown", this.handleTeamCardKeydown);
        card.removeEventListener("mouseenter", this.handleTeamCardMouseEnter);
        card.removeEventListener("mousemove", this.handleTeamCardMouseMove);
        card.removeEventListener("mouseleave", this.handleTeamCardMouseLeave);
      });
      this.boundTeamCards.clear();

      if (this.teamOverlay) {
        this.teamOverlay.removeEventListener("click", this.handleTeamOverlayClick);
      }

      if (this.blogGrid) {
        this.blogGrid.removeEventListener("pointermove", this.handleBlogPointerMove);
        this.blogGrid.removeEventListener("pointerleave", this.handleBlogPointerLeave);
      }
    }

    destroy() {
      this.unbindGlobalEvents();
      this.unbindResponsiveEvents();
      this.unbindDynamicEvents();

      window.clearTimeout(this.introTimer);
      window.clearTimeout(this.copyTimer);
      window.clearTimeout(this.blogClampTimer);

      if (this.blogParallaxFrame) {
        window.cancelAnimationFrame(this.blogParallaxFrame);
        this.blogParallaxFrame = null;
      }

      if (this.blogHighlightFrame) {
        window.cancelAnimationFrame(this.blogHighlightFrame);
        this.blogHighlightFrame = null;
      }

      this.disconnectBlogDecorParallax();
      this.root.dataset.mgInitialized = "false";
    }

    resetInstance() {
      const root = this.root;
      this.destroy();
      root.__mgLandingInstance = null;
      root.__mgLandingInstance = new MetagateImmersiveLanding(root);
    }

    handleSectionLoad(event) {
      const reloadedSection = event.target;

      if (
        reloadedSection !== this.root &&
        !reloadedSection.contains(this.root) &&
        !this.root.contains(reloadedSection)
      ) {
        return;
      }

      this.unbindDynamicEvents();
      this.refreshDynamicElements();
      this.prepareShatterText(true);
      this.bindDynamicEvents();
      this.setTeamCardLayout();
      this.refreshTeamCardMetrics();

      if (!this.isMobile) {
        this.initBlogCards(true);
      }

      this.closeTeamCard();
      this.requestTick();
    }

    handleScroll() {
      this.requestTick();
    }

    handleResize() {
      const currentMobile = this.mobileQuery.matches;

      if (currentMobile !== this.isMobile) {
        this.resetInstance();
        return;
      }

      this.refreshTeamCardMetrics();

      if (!this.isMobile && this.blogScene) {
        window.clearTimeout(this.blogClampTimer);
        this.blogClampTimer = window.setTimeout(() => {
          this.refreshDynamicElements();
          this.initBlogCards(true);
        }, 120);
      }

      this.requestTick();
    }

    handleMouseMove(event) {
      if (this.isMobile) return;

      const x = (event.clientX / window.innerWidth) * 2 - 1;
      const y = (event.clientY / window.innerHeight) * 2 - 1;

      this.mouse.targetX = clamp(x, -1, 1);
      this.mouse.targetY = clamp(y, -1, 1);

      this.requestTick();
    }

    handleHotspotEnter(event) {
      const hotspot = event.currentTarget;
      const x = hotspot.dataset.x || 50;
      const y = hotspot.dataset.y || 50;

      this.root.classList.add("is-hotspot-active");
      this.root.style.setProperty("--active-x", `${x}%`);
      this.root.style.setProperty("--active-y", `${y}%`);
    }

    handleHotspotLeave() {
      this.root.classList.remove("is-hotspot-active");
    }

    handleShatterEnter(event) {
      if (this.prefersReducedMotion || this.isMobile) return;
      event.currentTarget.classList.add("is-active");
    }

    handleShatterLeave(event) {
      event.currentTarget.classList.remove("is-active");
    }

    handleTeamCardClick(event) {
      const clickedLink = event.target.closest("a");
      if (clickedLink) return;

      const card = event.currentTarget;

      if (this.isMobile) return;

      event.stopPropagation();

      if (card.classList.contains("is-open")) return;
      this.openTeamCard(card);
    }

    handleTeamCardKeydown(event) {
      if (event.key !== "Enter" && event.key !== " ") return;
      if (this.isMobile) return;

      event.preventDefault();
      this.openTeamCard(event.currentTarget);
    }

    handleTeamCardMouseEnter(event) {
      if (this.isMobile) return;

      const card = event.currentTarget;
      if (!card.classList.contains("is-open")) return;

      const hoverImage = card.querySelector(".mg-team-card__image--hover");
      if (!hoverImage || !hoverImage.currentSrc) return;

      const media = card.querySelector(".mg-team-card__media");
      if (!media) return;

      const rect = media.getBoundingClientRect();
      const x = event.clientX;
      const y = event.clientY;

      const isInsideMedia =
        x >= rect.left &&
        x <= rect.right &&
        y >= rect.top &&
        y <= rect.bottom;

      if (isInsideMedia) {
        card.classList.add("is-image-hovered");
      }
    }

    handleTeamCardMouseMove(event) {
      if (this.isMobile) return;

      const card = event.currentTarget;
      if (!card.classList.contains("is-open")) return;

      const hoverImage = card.querySelector(".mg-team-card__image--hover");
      if (!hoverImage || !hoverImage.currentSrc) return;

      const media = card.querySelector(".mg-team-card__media");
      if (!media) return;

      const rect = media.getBoundingClientRect();
      const x = event.clientX;
      const y = event.clientY;

      const isInsideMedia =
        x >= rect.left &&
        x <= rect.right &&
        y >= rect.top &&
        y <= rect.bottom;

      card.classList.toggle("is-image-hovered", isInsideMedia);
    }

    handleTeamCardMouseLeave(event) {
      event.currentTarget.classList.remove("is-image-hovered");
    }

    handleTeamOverlayClick() {
      if (this.isMobile) return;
      this.closeTeamCard();
    }

    handleDocumentKeydown(event) {
      if (event.key === "Escape") {
        this.closeTeamCard();
      }
    }

    handleDocumentClick(event) {
      if (this.isMobile) return;
      if (!this.root.classList.contains("is-team-card-open")) return;

      const openCard = this.root.querySelector(".mg-team-card.is-open");
      if (!openCard) return;

      if (event.target.closest(".mg-team-card.is-open")) return;

      this.closeTeamCard();
    }

    handleBlogPointerMove(event) {
      if (this.isMobile) return;

      this.blogPointerX = event.clientX;
      this.blogPointerY = event.clientY;

      this.updateBlogHighlights();
      this.updateBlogParallax();
    }

    handleBlogPointerLeave() {
      this.blogCards.forEach((card) => {
        card.style.setProperty("--mg-blog-hl", "0");
        card.style.setProperty("--mg-blog-px", "0px");
        card.style.setProperty("--mg-blog-py", "0px");
      });
    }

    setTeamCardLayout() {
      if (this.isMobile) {
        this.teamCards.forEach((card, index) => {
          card.style.setProperty("--card-order", `${index + 1}`);
          card.style.setProperty("--card-x", "0px");
          card.style.setProperty("--card-y", "0px");
          card.style.setProperty("--card-rotate", "0deg");
          card.style.setProperty("--card-scale", "1");
        });
        return;
      }

      this.teamCards.forEach((card, index) => {
        const total = this.teamCards.length;
        const center = (total - 1) / 2;
        const offset = index - center;
        const cardX = offset * 100;
        const cardY = Math.abs(offset) * 18;
        const cardRotate = offset * 11;
        const cardScale = 1 - Math.abs(offset) * 0.02;

        card.style.setProperty("--card-order", `${index + 1}`);
        card.style.setProperty("--card-x", `${cardX}px`);
        card.style.setProperty("--card-y", `${cardY}px`);
        card.style.setProperty("--card-rotate", `${cardRotate}deg`);
        card.style.setProperty("--card-scale", `${cardScale}`);
      });
    }

    openTeamCard(card) {
      if (!card || this.isMobile) return;

      this.refreshTeamCardMetrics();
      this.root.classList.add("is-team-card-open");

      this.teamCards.forEach((item) => {
        item.classList.remove("is-open", "is-image-hovered");
        item.setAttribute("aria-expanded", "false");
      });

      card.classList.add("is-open");
      card.setAttribute("aria-expanded", "true");

      const hoverImage = card.querySelector(".mg-team-card__image--hover");
      if (!hoverImage || !hoverImage.currentSrc) return;

      const rect = card.getBoundingClientRect();
      const mouseX = window.innerWidth * ((this.mouse.x + 1) / 2);
      const mouseY = window.innerHeight * ((this.mouse.y + 1) / 2);

      const isPointerInside =
        mouseX >= rect.left &&
        mouseX <= rect.right &&
        mouseY >= rect.top &&
        mouseY <= rect.bottom;

      if (isPointerInside) {
        card.classList.add("is-image-hovered");
      }
    }

    closeTeamCard() {
      this.root.classList.remove("is-team-card-open");

      this.teamCards.forEach((card) => {
        card.classList.remove("is-open", "is-image-hovered");
        card.setAttribute("aria-expanded", "false");
      });
    }

    refreshTeamCardMetrics() {
      const stageRect = this.teamStage ? this.teamStage.getBoundingClientRect() : null;
      const width = stageRect?.width || 295;
      const height = stageRect?.height || 415;

      this.teamCards.forEach((card) => {
        card.style.setProperty("--open-card-width", `${width}px`);
        card.style.setProperty("--open-card-height", `${height}px`);
      });
    }

    prepareShatterText(force = false) {
      this.shatterLines = [...this.root.querySelectorAll(".js-shatter, .js-room-shatter")];

      this.shatterLines.forEach((line) => {
        if (line.dataset.prepared === "true" && !force) return;

        const text = (line.textContent || "").trim();
        line.textContent = "";

        const words = text.split(/\s+/);

        words.forEach((word) => {
          const wordWrap = document.createElement("span");
          wordWrap.className = "mg-hero-shatter__word";

          [...word].forEach((char) => {
            const span = document.createElement("span");
            span.className = "mg-hero-shatter__char";
            span.textContent = char;

            const scatterX = (Math.random() * 220 - 110).toFixed(1);
            const scatterY = (Math.random() * 140 - 70).toFixed(1);
            const scatterR = (Math.random() * 70 - 35).toFixed(1);

            span.style.setProperty("--scatter-x", `${scatterX}px`);
            span.style.setProperty("--scatter-y", `${scatterY}px`);
            span.style.setProperty("--scatter-r", `${scatterR}deg`);

            wordWrap.appendChild(span);
          });

          line.appendChild(wordWrap);
        });

        line.dataset.prepared = "true";
      });
    }

    initBlogCards(force = false) {
      if (this.isMobile || !this.blogGrid || !this.blogCards.length) return;
      if (this.blogGrid.dataset.mgBlogPrepared === "true" && !force) return;

      const gridRect = this.blogGrid.getBoundingClientRect();
      const stageWidth = gridRect.width || this.blogGrid.offsetWidth || 1200;
      const stageHeight = gridRect.height || this.blogGrid.offsetHeight || 560;
      const seed = hashStr(`${this.root.dataset.sectionId || this.root.id || "mg"}::blog`);
      const rnd = makeRand(seed);

      const autoCards = this.blogCards.filter((card) => {
        const autoValue = (card.getAttribute("data-mg-blog-auto-pos") || "").trim();
        return autoValue === "true";
      });

      const placed = [];
      const minDistance = 15;

      const tooClose = (x, y) => {
        for (let i = 0; i < placed.length; i += 1) {
          const dx = x - placed[i].x;
          const dy = y - placed[i].y;
          if (dx * dx + dy * dy < minDistance * minDistance) return true;
        }
        return false;
      };

      if (autoCards.length) {
        const aspect = clamp(stageWidth / Math.max(stageHeight, 1), 0.7, 2);
        const count = autoCards.length;
        const cols = Math.ceil(Math.sqrt(count * aspect));
        const rows = Math.ceil(count / Math.max(cols, 1));
        const pad = 0.08;
        const map01ToPct = (value) => (pad + value * (1 - pad * 2)) * 100;

        autoCards.forEach((card, index) => {
          const cellX = index % cols;
          const cellY = Math.floor(index / cols);

          let px = 50;
          let py = 50;
          let tries = 0;

          do {
            const jx = (cellX + rnd()) / cols;
            const jy = (cellY + rnd()) / rows;
            px = map01ToPct(jx);
            py = map01ToPct(jy);
            tries += 1;
          } while (tries < 18 && tooClose(px, py));

          placed.push({ x: px, y: py });

          card.style.setProperty("--mg-blog-x", `${px.toFixed(2)}%`);
          card.style.setProperty("--mg-blog-y", `${py.toFixed(2)}%`);
        });
      }

      this.blogCards.forEach((card) => {
        const computed = window.getComputedStyle(card);

        let xp = parseFloat(computed.getPropertyValue("--mg-blog-x"));
        let yp = parseFloat(computed.getPropertyValue("--mg-blog-y"));

        if (!Number.isFinite(xp)) xp = 50;
        if (!Number.isFinite(yp)) yp = 50;

        xp = clamp(xp, 0, 100);
        yp = clamp(yp, 0, 100);

        const rect = card.getBoundingClientRect();
        const cardWidth = rect.width || parseFloat(computed.getPropertyValue("--mg-blog-w")) || 360;
        const cardHeight = rect.height || parseFloat(computed.getPropertyValue("--mg-blog-h")) || 190;

        const minX = cardWidth / 2;
        const maxX = Math.max(minX, stageWidth - cardWidth / 2);
        const minY = cardHeight / 2;
        const maxY = Math.max(minY, stageHeight - cardHeight / 2);

        const leftPx = minX + (xp / 100) * (maxX - minX);
        const topPx = minY + (yp / 100) * (maxY - minY);

        card.style.setProperty("--mg-blog-left", `${leftPx.toFixed(2)}px`);
        card.style.setProperty("--mg-blog-top", `${topPx.toFixed(2)}px`);
      });

      this.blogGrid.dataset.mgBlogPrepared = "true";
      this.initBlogDecorParallax();
    }

    initBlogDecorParallax() {
      if (this.isMobile || this.prefersReducedMotion) return;
      if (this.root.dataset.mgDecorBound === "true") return;

      const decorItems = this.root.querySelectorAll("[data-mg-blog-decor] .mg-blog-decor__item");
      if (!decorItems.length) return;

      const handler = (event) => {
        const x = (event.clientX / window.innerWidth - 0.5) * 10;
        const y = (event.clientY / window.innerHeight - 0.5) * 10;

        decorItems.forEach((el) => {
          el.style.transform = `translate(-50%, -50%) translate(${x}px, ${y}px)`;
        });
      };

      this.root.__mgDecorHandler = handler;
      window.addEventListener("mousemove", handler, { passive: true });
      this.root.dataset.mgDecorBound = "true";
    }

    disconnectBlogDecorParallax() {
      if (this.root.__mgDecorHandler) {
        window.removeEventListener("mousemove", this.root.__mgDecorHandler);
        this.root.__mgDecorHandler = null;
      }

      this.root.dataset.mgDecorBound = "false";
    }

    updateBlogParallax() {
      if (!this.blogPointerEnabled || !this.blogCards.length) return;
      if (this.blogParallaxFrame) return;

      this.blogParallaxFrame = window.requestAnimationFrame(() => {
        this.blogParallaxFrame = null;

        this.blogCards.forEach((card) => {
          const rect = card.getBoundingClientRect();
          const x = (this.blogPointerX - rect.left) / rect.width;
          const y = (this.blogPointerY - rect.top) / rect.height;
          const nx = clamp((x - 0.5) * 2, -1, 1);
          const ny = clamp((y - 0.5) * 2, -1, 1);
          const moveFactor = parseFloat(card.dataset.mgBlogMoveFactor || "1");
          const maxMove = 12 * moveFactor;

          card.style.setProperty("--mg-blog-px", `${(nx * maxMove).toFixed(2)}px`);
          card.style.setProperty("--mg-blog-py", `${(ny * maxMove).toFixed(2)}px`);
        });
      });
    }

    updateBlogHighlights() {
      if (!this.blogPointerEnabled || !this.blogCards.length) return;
      if (this.blogHighlightFrame) return;

      this.blogHighlightFrame = window.requestAnimationFrame(() => {
        this.blogHighlightFrame = null;

        const radius = 220;

        this.blogCards.forEach((card) => {
          const rect = card.getBoundingClientRect();
          const cx = clamp(this.blogPointerX, rect.left, rect.right);
          const cy = clamp(this.blogPointerY, rect.top, rect.bottom);
          const dx = this.blogPointerX - cx;
          const dy = this.blogPointerY - cy;
          const dist = Math.sqrt(dx * dx + dy * dy);
          const t = clamp(1 - dist / radius, 0, 1);

          card.style.setProperty("--mg-blog-hl", t.toFixed(3));
          card.style.setProperty("--mg-blog-mx", `${(this.blogPointerX - rect.left).toFixed(1)}px`);
          card.style.setProperty("--mg-blog-my", `${(this.blogPointerY - rect.top).toFixed(1)}px`);
        });
      });
    }

    requestTick() {
      if (this.ticking) return;

      this.ticking = true;

      window.requestAnimationFrame(() => {
        this.ticking = false;
        this.render();
      });
    }

    render() {
      const rect = this.root.getBoundingClientRect();
      const totalScrollable = Math.max(this.root.offsetHeight - window.innerHeight, 1);
      const progress = clamp(-rect.top / totalScrollable, 0, 1);

      if (this.isMobile) {
        this.renderMobile(progress);
        return;
      }

      this.renderDesktop(progress);
    }

  renderMobile(progress) {
  this.mouse.x = lerp(this.mouse.x, 0, 0.1);
  this.mouse.y = lerp(this.mouse.y, 0, 0.1);

  const heroZoomPhase = easeInOut(clamp(progress / 0.34, 0, 1));
  const heroZoom = lerp(1, 3.25, heroZoomPhase);

  const heroOpacity = 1 - easeInOut(clamp((progress - 0.22) / 0.12, 0, 1));
  const heroBlur = lerp(
    0,
    10,
    easeInOut(clamp((progress - 0.18) / 0.14, 0, 1))
  );

  const roomOpacity = easeOut(clamp((progress - 0.16) / 0.18, 0, 1));
  const roomActive = roomOpacity > 0.02;

  const copyFadeProgress = clamp((progress - 0.02) / 0.18, 0, 1);
  const copyOpacity = 1 - easeInOut(copyFadeProgress);
  const scrollHintOpacity = 1 - clamp(progress * 4.5, 0, 1);

  this.root.classList.toggle("is-room-active", progress > 0.16);

  this.root.classList.remove("is-team-active");
  this.root.classList.remove("is-blog-active");
  this.root.classList.remove("is-team-card-open");

  if (progress > 0.60) {
    this.root.classList.add("is-static-after-room");
  } else {
    this.root.classList.remove("is-static-after-room");
  }

  this.root.style.setProperty("--mg-mx", this.mouse.x.toFixed(4));
  this.root.style.setProperty("--mg-my", this.mouse.y.toFixed(4));
  this.root.style.setProperty("--copy-opacity", copyOpacity.toFixed(4));
  this.root.style.setProperty("--hero-zoom", heroZoom.toFixed(4));
  this.root.style.setProperty("--hero-opacity", heroOpacity.toFixed(4));
  this.root.style.setProperty("--hero-blur", `${heroBlur.toFixed(2)}px`);

  this.root.style.setProperty("--room-opacity", roomOpacity.toFixed(4));
  this.root.style.setProperty("--room-clip", "0%");

  this.root.style.setProperty("--team-opacity", "0");
  this.root.style.setProperty("--blog-opacity", "0");

  this.root.style.setProperty("--scroll-hint-opacity", scrollHintOpacity.toFixed(4));
  this.root.style.setProperty("--exit-opacity", "1");
  this.root.style.setProperty("--exit-scale", "1");
}


    renderDesktop(progress) {
      this.mouse.x = lerp(this.mouse.x, this.mouse.targetX, 0.12);
      this.mouse.y = lerp(this.mouse.y, this.mouse.targetY, 0.12);

      const heroZoomPhase = easeInOut(clamp(progress / 0.34, 0, 1));
      const heroZoom = lerp(1, 5.1, heroZoomPhase);

      const heroOpacity = 1 - easeInOut(clamp((progress - 0.26) / 0.10, 0, 1));
      const heroBlur = lerp(0, 16, easeInOut(clamp((progress - 0.24) / 0.14, 0, 1)));

      const roomOpacityIn = easeOut(clamp((progress - 0.24) / 0.12, 0, 1));
      const roomOpacityOut = 1 - easeInOut(clamp((progress - 0.60) / 0.08, 0, 1));
      const roomOpacity = clamp(roomOpacityIn * roomOpacityOut, 0, 1);
      const roomActive = roomOpacity > 0.08;

      const roomReveal = easeInOut(clamp((progress - 0.32) / 0.34, 0, 1));
      const roomClip = `${lerp(170, 0, roomReveal)}%`;

      const teamOpacityIn = easeOut(clamp((progress - 0.72) / 0.08, 0, 1));
      const teamOpacityOut = 1 - easeInOut(clamp((progress - 0.84) / 0.08, 0, 1));
      const teamOpacity = clamp(teamOpacityIn * teamOpacityOut, 0, 1);
      const teamActive = teamOpacity > 0.08;

      const blogOpacityIn = easeOut(clamp((progress - 0.96) / 0.08, 0, 1));
      const blogOpacity = clamp(blogOpacityIn, 0, 1);
      const blogActive = blogOpacity > 0.08;

      const copyFadeProgress = clamp((progress - 0.02) / 0.18, 0, 1);
      const copyOpacity = 1 - easeInOut(copyFadeProgress);
      const scrollHintOpacity = 1 - clamp(progress * 4.5, 0, 1);

      if (window.Shopify?.designMode) {
        this.root.style.setProperty("--copy-opacity", "1");
      } else {
        this.root.style.setProperty("--copy-opacity", copyOpacity.toFixed(4));
      }

      this.root.classList.toggle("is-room-active", roomActive);
      this.root.classList.toggle("is-team-active", teamActive);
      this.root.classList.toggle("is-blog-active", blogActive);

      if (!teamActive && this.root.classList.contains("is-team-card-open")) {
        this.closeTeamCard();
      }

      this.root.style.setProperty("--mg-mx", this.mouse.x.toFixed(4));
      this.root.style.setProperty("--mg-my", this.mouse.y.toFixed(4));
      this.root.style.setProperty("--hero-zoom", heroZoom.toFixed(4));
      this.root.style.setProperty("--hero-opacity", heroOpacity.toFixed(4));
      this.root.style.setProperty("--hero-blur", `${heroBlur.toFixed(2)}px`);
      this.root.style.setProperty("--room-opacity", roomOpacity.toFixed(4));
      this.root.style.setProperty("--room-clip", roomClip);
      this.root.style.setProperty("--team-opacity", teamOpacity.toFixed(4));
      this.root.style.setProperty("--blog-opacity", blogOpacity.toFixed(4));
      this.root.style.setProperty("--scroll-hint-opacity", scrollHintOpacity.toFixed(4));
      this.root.style.setProperty("--exit-opacity", "1");
      this.root.style.setProperty("--exit-scale", "1");
    }
  }

  const initMetagateSections = (scope = document) => {
    const sections = scope.querySelectorAll(".mg-immersive");

    sections.forEach((section) => {
      if (!section.__mgLandingInstance) {
        section.__mgLandingInstance = new MetagateImmersiveLanding(section);
      }
    });
  };

  const destroyMetagateSections = (scope = document) => {
    const sections = scope.querySelectorAll(".mg-immersive");

    sections.forEach((section) => {
      if (section.__mgLandingInstance) {
        section.__mgLandingInstance.destroy();
        section.__mgLandingInstance = null;
      }
    });
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initMetagateSections(document));
  } else {
    initMetagateSections(document);
  }

  document.addEventListener("shopify:section:load", (event) => {
    initMetagateSections(event.target);
  });

  document.addEventListener("shopify:section:unload", (event) => {
    destroyMetagateSections(event.target);
  });
})();
